import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCard from '@/components/base/ProductCard';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import CTABanner from '@/components/feature/CTABanner';
import FAQAccordion from '@/components/base/FAQAccordion';
import { chairs } from '@/mocks/products';
import CuradoriaGaleria from '@/components/feature/CuradoriaGaleria';
import { cadeiras as cadeirasCuradoria } from '@/mocks/curadoria';
import { useSEO } from '@/hooks/useSEO';
import { useLazySection } from '@/hooks/useLazySection';

const faqs = [
  { q: 'As cadeiras da Home Office Premium são certificadas pela NR-17?', a: 'Sim. Todos os modelos de cadeiras ergonômicas Frisokar e Plaxmetal comercializados pela Home Office Premium atendem os requisitos da NR-17 (Norma Regulamentadora nº 17 do Ministério do Trabalho). A norma exige regulagem de altura do assento, suporte lombar ajustável, bordas arredondadas, tecido respirável e base com 5 rodas giratórias. Todos esses itens estão presentes nos modelos disponibilizados.' },
  { q: 'O que a NR-17 exige especificamente para cadeiras de trabalho?', a: 'A NR-17 determina que cadeiras usadas em postos de trabalho com operações sentadas devem ter: altura do assento ajustável à estatura do trabalhador, encosto com suporte à região lombar, borda frontal do assento arredondada, superfície acolchoada e não escorregadia. Para trabalhos com computador, ainda é recomendado apoio para os braços e encosto reclinável. Nossa linha cobre todos esses requisitos.' },
  { q: 'Qual o impacto de cadeiras inadequadas no afastamento de funcionários?', a: 'Cadeiras sem ergonomia são uma das principais causas de LER/DORT (Lesões por Esforços Repetitivos e Distúrbios Osteomusculares Relacionados ao Trabalho), que respondem por grande parte dos afastamentos pelo INSS no Brasil. Além do custo humano, a empresa pode sofrer ações trabalhistas e autuações da Fiscalização do Trabalho. Investir em cadeiras NR-17 é prevenção ativa e pode reduzir em até 40% o índice de afastamentos por dor postural.' },
  { q: 'Quais mecanismos de regulagem as cadeiras ergonômicas precisam ter?', a: 'As cadeiras ergonômicas devem ter no mínimo: regulagem de altura do assento (por pistão a gás), regulagem do encosto lombar (altura e tensão), regulagem do apoio de braço (altura e largura nos modelos executivos), e mecanismo de inclinação do encosto (relax ou sincronizado). Os modelos Frisokar Diretor e Executivo possuem todos esses mecanismos. Os modelos operacionais atendem o mínimo exigido pela NR-17.' },
  { q: 'Qual é a garantia das cadeiras Frisokar e Plaxmetal?', a: 'As cadeiras Frisokar possuem garantia de 5 anos contra defeitos de fabricação na estrutura e base, e 1 ano para componentes mecânicos como molas e pistão. A Plaxmetal oferece garantia de 2 anos na estrutura e 1 ano em componentes. A Home Office Premium presta assistência técnica local em Caruaru PE, sem precisar enviar a peça para outra cidade — diferencial importante para empresas com grande quantidade de cadeiras.' },
  { q: 'Como fazer um diagnóstico ergonômico antes de comprar cadeiras para a empresa?', a: 'A Home Office Premium oferece visita técnica gratuita em Caruaru PE para empresas interessadas em adequar o mobiliário à NR-17. Durante a visita, avaliamos os postos de trabalho, o perfil dos funcionários e indicamos os modelos mais adequados. Após a visita, entregamos um relatório com recomendações e orçamento detalhado.' },
  { q: 'Vocês atendem empresas com grande volume de cadeiras para Caruaru e região?', a: 'Sim! Temos condições especiais para pedidos acima de 10 unidades, com desconto progressivo, prazo diferenciado e entrega + montagem inclusa. Atendemos empresas em Caruaru, Bezerros, Gravatá, Santa Cruz do Capibaribe, Garanhuns, Arcoverde e toda a região do Agreste e Sertão de Pernambuco.' },
  { q: 'Qual é a diferença entre cadeira operacional, executiva e presidencial?', a: 'Cadeira operacional: encosto baixo ou médio, sem apoio de braço ou com apoio fixo, indicada para call center, recepção e funcionários em geral. Cadeira executiva: encosto alto, apoio de braço regulável, geralmente em couro ou PU, para analistas e coordenadores. Cadeira presidencial: encosto ultra-alto (high back), espuma de alta densidade, couro genuíno ou ecológico premium, mecanismo relax — para direções e salas de reunião de alto padrão.' },
];

export default function CadeirasPage() {
  const [gridRef, gridVisible] = useLazySection<HTMLDivElement>();

  useSEO({
    title: 'Cadeiras Ergonômicas NR-17 em Caruaru PE | Frisokar e Plaxmetal',
    description: 'Cadeiras ergonômicas de escritório em Caruaru PE. Modelos executivos, operacionais e presidenciais Frisokar e Plaxmetal com certificação NR-17, garantia de 5 anos e entrega em todo Pernambuco.',
    keywords: 'cadeiras ergonômicas Caruaru, cadeiras NR-17 Pernambuco, Frisokar Recife, Plaxmetal Petrolina, cadeiras executivas Agreste, cadeiras escritório Nordeste, cadeiras presidenciais Recife, cadeiras operacionais Caruaru',
    path: '/cadeiras',
    ogTitle: 'Cadeiras Ergonômicas NR-17 em Caruaru PE | Home Office Premium',
    ogDescription: 'Cadeiras ergonômicas Frisokar e Plaxmetal em Caruaru PE. Certificação NR-17, garantia de 5 anos e entrega em todo PE.',
    dateModified: '2026-04-27',
  });

  useEffect(() => {
    const schemaId = 'schema-cadeiras';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Cadeiras de Escritório Ergonômicas – Home Office Premium',
      description: 'Cadeiras ergonômicas de escritório em Caruaru PE. Modelos executivos, operacionais e presidenciais das marcas Frisokar e Plaxmetal.',
      url: `${siteUrl}/cadeiras`,
    });

    const breadcrumbId = 'schema-cadeiras-breadcrumb';
    let breadcrumbScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!breadcrumbScript) { breadcrumbScript = document.createElement('script'); breadcrumbScript.id = breadcrumbId; breadcrumbScript.type = 'application/ld+json'; document.head.appendChild(breadcrumbScript); }
    breadcrumbScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Produtos', item: `${siteUrl}/produtos` },
        { '@type': 'ListItem', position: 3, name: 'Cadeiras Ergonômicas', item: `${siteUrl}/cadeiras` },
      ],
    });

    const itemListSchemaId = 'schema-cadeiras-itemlist';
    let itemListScript = document.getElementById(itemListSchemaId) as HTMLScriptElement | null;
    if (!itemListScript) { itemListScript = document.createElement('script'); itemListScript.id = itemListSchemaId; itemListScript.type = 'application/ld+json'; document.head.appendChild(itemListScript); }
    itemListScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Cadeiras Ergonômicas para Escritório – Home Office Premium Caruaru',
      url: `${siteUrl}/cadeiras`,
      numberOfItems: chairs.length,
      itemListElement: chairs.map((chair, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Product',
          name: chair.name,
          description: chair.description,
          image: chair.image,
          brand: { '@type': 'Brand', name: chair.brand },
          offers: {
            '@type': 'Offer',
            priceCurrency: 'BRL',
            price: chair.price.toFixed(2),
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: 'Home Office Premium' },
            url: `${siteUrl}/contato`,
          },
        },
      })),
    });
    const faqId = 'schema-cadeiras-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });

    return () => { script?.remove(); breadcrumbScript?.remove(); itemListScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        eyebrow="Frisokar · Plaxmetal"
        eyebrowIcon="ri-armchair-line"
        title={
          <>
            Cadeiras que cuidam de quem <span className="text-white/50 font-light">produz o dia inteiro.</span>
          </>
        }
        subtitle="Ergonomia certificada NR-17 para longas jornadas. Modelos operacionais, executivos e presidenciais das melhores marcas, com garantia de até 5 anos."
        image="/media/ia/0189.webp"
        alt="Cadeiras ergonômicas de escritório Frisokar e Plaxmetal em Caruaru PE"
        breadcrumb={[
          { label: 'Home', to: '/' },
          { label: 'Produtos', to: '/produtos' },
          { label: 'Cadeiras Ergonômicas' },
        ]}
        ctas={[
          { label: 'Solicitar orçamento', to: '/contato' },
          { label: 'Falar no WhatsApp', href: 'https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es', variant: 'secondary' },
        ]}
      />

      {/* Por que ergonomia importa */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <SectionHeading
                eyebrow="Por que ergonomia"
                title="Uma cadeira certa muda a produtividade do seu time."
                subtitle="Cadeiras inadequadas são uma das principais causas de afastamentos por LER/DORT. Investir em ergonomia é prevenir custos, ações trabalhistas e quedas de rendimento."
              />
              <div className="mt-8 space-y-4">
                {[
                  { icon: 'ri-shield-check-line', text: 'Certificação NR-17 em todos os modelos' },
                  { icon: 'ri-medal-line', text: 'Garantia de até 5 anos na estrutura' },
                  { icon: 'ri-tools-line', text: 'Assistência técnica local em Caruaru' },
                  { icon: 'ri-user-heart-line', text: 'Diagnóstico ergonômico gratuito' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-600 flex-shrink-0">
                      <i className={item.icon}></i>
                    </span>
                    <span className="text-foreground-700 text-[15px]">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="img-zoom rounded-2xl overflow-hidden">
              <img
                src="/media/ia/0190.webp"
                alt="Ambiente corporativo com cadeiras ergonômicas"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Modelos */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <SectionHeading
              eyebrow="Catálogo"
              title="Modelos disponíveis."
              subtitle={`${chairs.length} cadeiras ergonômicas para cada posto de trabalho.`}
            />
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/frisokar" className="text-[13px] font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap transition-colors cursor-pointer">
                Frisokar
              </Link>
              <span className="text-foreground-300">·</span>
              <Link to="/plaxmetal" className="text-[13px] font-medium text-foreground-500 hover:text-primary-600 whitespace-nowrap transition-colors cursor-pointer">
                Plaxmetal
              </Link>
            </div>
          </div>

          <div ref={gridRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-12" data-product-shop>
            {gridVisible && chairs.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      <CuradoriaGaleria
        eyebrow="Curadoria de fabricante · Plaxmetal"
        titulo="As linhas que sustentam a jornada."
        descricao="Bix X+, Darix X+, linha colaborativa, direção, poltronas Elyze e assentos modulares Rizoma. Modelo, aplicação e página do catálogo identificados em cada item."
        produtos={cadeirasCuradoria}
        contexto="cadeiras"
        tom="soft"
      />

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading
            eyebrow="Dúvidas frequentes"
            title="NR-17, ergonomia e garantia."
            className="mb-12"
          />
          <div className="max-w-3xl">
            {faqs.map((f) => (
              <FAQAccordion key={f.q} question={f.q} answer={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Complete seu escritório */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading
            eyebrow="Continue explorando"
            title="Complete seu escritório corporativo."
            className="mb-10"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'Mesas corporativas', sub: 'Marzo Vitorino', to: '/mesas', icon: 'ri-layout-grid-line' },
              { label: 'Divisórias modulares', sub: 'Sem obra', to: '/divisorias', icon: 'ri-layout-masonry-line' },
              { label: 'Cabines acústicas', sub: 'Isolamento até 45dB', to: '/cabines-acusticas', icon: 'ri-volume-mute-line' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group flex items-center gap-4 bg-white rounded-xl border border-background-200/60 p-5 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 group-hover:text-primary-600 transition-colors flex-shrink-0">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <span>
                  <span className="block text-foreground-900 font-medium text-[14px] group-hover:text-primary-600 transition-colors">{item.label}</span>
                  <span className="block text-foreground-400 text-[12px] font-light">{item.sub}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Precisa equipar sua equipe inteira?"
        subtitle="Fazemos o diagnóstico ergonômico e montamos o orçamento com condições especiais para pedidos acima de 10 unidades."
        primaryLabel="Falar com um especialista"
        primaryHref="https://wa.me/5581982030285?text=Ol%C3%A1%20Ednilson!%20Vi%20o%20site%20da%20HomeOffice%20Premium.%20Gostaria%20de%20conversar%20sobre%20cadeiras%20ergon%C3%B4micas."
      />

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}